/**
 * JoinSplit Type Definitions
 * Railgun-style UTXO model with EdDSA signatures
 */

/**
 * Spending keys - the master secret from which all other keys are derived
 */
export interface SpendingKeys {
  // The master spending private key (never leaves device)
  spendingKey: Uint8Array;
  // Optional 128-bit recovery entropy for new 12-word wallets. Secret; local only.
  recoveryEntropy?: Uint8Array;
  /** Global phrase entropy is separate from the derived network spending key. */
  masterRecovery?: import("../joinsplit-v2/masterRecoveryConnection").MasterRecoveryMetadata;
  // EdDSA public key on BabyJubJub curve (Ax, Ay)
  publicKey: [bigint, bigint];
  // Key used to compute nullifiers - derived from spending key
  nullifyingKey: bigint;
  // Master Public Key = Poseidon(Ax, Ay, nullifyingKey)
  mpk: bigint;
  // Baby Jubjub scalar `a` with publicKey = a * Base8. The circuit proves
  // knowledge of this; it must never leave the device or be shared as a
  // viewing key.
  spendSecretScalar: bigint;
}

/**
 * Viewing keys - can view notes but not spend
 */
export interface ViewingKeys {
  // Master Public Key (can be shared with accountants)
  mpk: bigint;
  // Public key components
  publicKey: [bigint, bigint];
}

/**
 * Shareable viewing key payload.
 * Encoded as hex(JSON) so admins/compliance tooling can load it without spend authority.
 */
export interface ShareableViewingKeyPayload {
  version: 1;
  mpk: string;
  publicKey: [string, string];
}

/**
 * EdDSA signature on BabyJubJub curve
 */
export interface EdDSASignature {
  R8: [bigint, bigint]; // R8x, R8y
  S: bigint;
}

/**
 * A JoinSplit note (UTXO)
 * Unlike the simple model, nullifier is deterministic from tokenHash + leafIndex
 */
export interface JoinSplitNote {
  // Wallet record namespace. Flat V2 notes must carry these fields so inbox
  // data cannot be confused across chains, pools, or settlement versions.
  schemaVersion?: 2;
  protocol?: "flat-joinsplit-v2";
  chainId?: number;
  poolAddress?: string;
  // Non-EVM notes name their settlement network and circuit chain domain
  // (a 0x-prefixed 32-byte word) instead of an EVM chain id; `poolAddress`
  // then holds the pool program id.
  network?: string;
  chainDomainId?: string;
  // Note Public Key = Poseidon(MPK, random)
  npk: bigint;
  // Random value used to derive NPK (needed for spending)
  random: bigint;
  // The note commitment = Poseidon(NPK, tokenHash, value)
  commitment: string;
  // Token hash = Poseidon(tokenAddress)
  tokenHash: bigint;
  // Optional token identity metadata (required for NFT and non-legacy assets)
  tokenData?: NoteTokenData;
  // Value in wei
  value: bigint;
  // Leaf index in Merkle tree (needed for nullifier derivation)
  leafIndex: number;
  // Merkle tree index (for historical trees)
  treeIndex?: number;
  // Explicit tree identifier for deterministic lookup (Base/EVM)
  treeKey?: string;
  // Explicit tree PDA (Solana)
  treePda?: string;
  // Merkle root observed when the note was indexed.
  merkleRoot?: string;
  // Computed nullifier = Poseidon(nullifyingKey, tokenHash, leafIndex)
  nullifier?: string;
  // Optional encrypted UI history metadata. This is never part of the
  // commitment/proof and is only decrypted by the note recipient.
  privateHistory?: {
    type?: "deposit" | "transfer" | "withdraw" | "receive" | "swap";
    amount?: string;
    symbol?: string;
    memo?: string;
    txHash?: string;
    timestamp?: number;
  };
}

export enum NoteTokenType {
  ERC20 = 0,
  ERC721 = 1,
  ERC1155 = 2,
  SOL_SPL = 3,
  SOL_TOKEN2022 = 4,
  VNFT = 5,
}

export interface NoteTokenData {
  // EVM token address (0x...) OR Solana mint encoded as 0x-prefixed 32-byte hex.
  tokenAddress: string;
  tokenType: NoteTokenType;
  // NFT token id / sub-id. Keep "0" for fungible assets.
  tokenSubID: string;
  // Optional raw Solana mint (base58) for UX/storage convenience.
  tokenMint?: string;
  // Optional wrapped NFT metadata (encrypted payload only).
  wrappedAsset?: {
    claimHash: string;
    vaultIdx: string;
    nonce: string;
    vaultAddress?: string;
    tokenAddress: string;
    tokenType: NoteTokenType.ERC721 | NoteTokenType.ERC1155;
    tokenSubID: string;
    name?: string;
    symbol?: string;
    redacted?: boolean;
  };
}

/**
 * Encrypted note for storage/transmission
 */
export interface EncryptedJoinSplitNote {
  // Ephemeral public key for ECDH
  ephemeralPubKey: string;
  // AES-GCM encrypted note data
  ciphertext: string;
  iv: string;
  authTag: string;
  // Public data (needed for tree operations)
  commitment: string;
  tokenHash: string;
  leafIndex: number;
  treeIndex?: number;
  treeKey?: string;
  treePda?: string;
}

/**
 * Stored note with metadata
 */
export interface StoredJoinSplitNote {
  note: JoinSplitNote;
  timestamp: number;
  txHash?: string;
  spent?: boolean;
}

/**
 * JoinSplit transaction inputs
 */
export interface JoinSplitInputs {
  // Public
  merkleRoot: bigint;
  boundParamsHash: bigint;
  nullifiers: bigint[];
  commitmentsOut: bigint[];

  // Private
  token: bigint;
  publicKey: [bigint, bigint];
  signature: [bigint, bigint, bigint]; // R8x, R8y, S
  randomIn: bigint[];
  valueIn: bigint[];
  pathElements: bigint[][];
  pathIndices: bigint[];
  nullifyingKey: bigint;
  npkOut: bigint[];
  valueOut: bigint[];
}

/**
 * Bound transaction parameters
 */
export interface BoundParams {
  // Chain ID
  chainId: number;
  // Recipient addresses (for withdrawals)
  recipients: string[];
  // Fee amounts
  fees: bigint[];
  // Timestamp/nonce for replay protection
  nonce: bigint;
}
